import { useNavigate, useOutletContext } from '@remix-run/react';
import { BadgePercent, ChevronRight, Loader2Icon, Plus } from 'lucide-react';
import { useState } from 'react';
import { Price } from '~/components/products/Price';
import { DetailedProductFragment } from '~/generated/graphql';
import { OutletContext } from '~/types';
import { calculateEffectivePrice } from '~/utils/pricing';

interface RedtabPickSectionProps {
  products: DetailedProductFragment[];
}

export function RedtabPickSection({ products }: RedtabPickSectionProps) {
  const navigate = useNavigate();

  return (
    <section className="w-full py-4 sm:py-6">
      <div className="flex flex-col rounded-[24px] p-4 sm:p-6 gap-2">
        {/* Section Header */}
        <div className="flex items-center justify-between">
          <h2 className="flex items-center">
            <img
              src="/redtab-pick.png"
              alt="Redtab Pick"
              className="h-6 sm:h-7 md:h-8 w-auto"
            />
          </h2>
          <button
            onClick={() => navigate('/search?q=')}
            className="text-sm font-semibold underline transition-colors"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '14px',
              lineHeight: '24px',
              color: '#1A090E',
              textDecoration: 'underline',
            }}
          >
            View all
          </button>
        </div>

        {/* Products Grid - 5 columns max */}
        <div className="grid grid-cols-2 min-[480px]:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 sm:gap-4">
          {products.map((product) => (
            <RedtabPickProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RedtabPickProductCard({
  product,
}: {
  product: DetailedProductFragment;
}) {
  const navigate = useNavigate();
  const { activeOrderFetcher: fetcher, merchantContextRequired } =
    useOutletContext<OutletContext>();
  const [quantity, setQuantity] = useState(1);

  const variant = product.variants[0];
  const isBeingAddedToCart =
    fetcher.state === 'submitting' &&
    fetcher.formData?.get('variantId') === variant?.id;

  const basePrice =
    typeof variant?.priceWithTax === 'number'
      ? variant.priceWithTax
      : (variant?.priceWithTax as any)?.value || 0;
  const currencyCode = variant?.currencyCode;

  const priceTiers = variant?.customFields?.priceTiers || [];

  const { effectiveUnitPrice } = calculateEffectivePrice(
    basePrice || 0,
    priceTiers,
    quantity,
  );

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!fetcher || !variant?.id) return;

    fetcher.submit(
      {
        action: 'addItemToOrder',
        variantId: variant.id,
        quantity: quantity.toString(),
      },
      {
        method: 'post',
        action: '/api/active-order',
      },
    );
  };

  const handleProductClick = () => {
    navigate(`/products/${product.slug}`);
  };

  return (
    <div className="flex flex-col h-full bg-transparent">
      {/* Image Container - Blue gradient like ProductCard */}
      <div className="relative">
        <div
          className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer flex items-center justify-center"
          onClick={handleProductClick}
        >
          {(variant?.customFields?.originalPrice ?? 0) > effectiveUnitPrice && (
            <div className="absolute top-0 left-0 bg-[#E80245] text-white text-xs font-bold px-3 py-1 font-serif z-10">
              Sale
            </div>
          )}
          {product.featuredAsset?.preview ? (
            <img
              src={`${product.featuredAsset.preview}?w=200&h=200&format=webp`}
              alt={product.name}
              className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center opacity-20">
              <img src="/brand_logo.png" alt="Logo" className="w-1/2 h-auto" />
            </div>
          )}
        </div>

        {/* Floating Plus Button - Bottom Right */}
        <button
          onClick={handleAddToCart}
          disabled={isBeingAddedToCart || merchantContextRequired}
          className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white border border-gray-100 shadow-lg flex items-center justify-center text-gray-600 hover:text-primary hover:border-primary/30 active:scale-95 transition-all duration-200 z-10"
        >
          {isBeingAddedToCart ? (
            <Loader2Icon size={18} className="animate-spin text-primary" />
          ) : (
            <Plus size={20} strokeWidth={2} />
          )}
        </button>
      </div>

      {/* Content */}
      <div className="pt-3 px-1 flex flex-col flex-1">
        {/* Price */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-gray-900 font-bold text-lg tracking-tight">
            <Price
              priceWithTax={effectiveUnitPrice}
              currencyCode={currencyCode}
            />
          </span>
          {(variant?.customFields?.originalPrice ?? 0) > effectiveUnitPrice && (
            <div className="relative inline-block">
              <span className="text-gray-400 text-xs">
                <Price
                  priceWithTax={variant?.customFields?.originalPrice ?? 0}
                  currencyCode={currencyCode}
                />
              </span>
              <div className="absolute top-1/2 left-0 w-full h-[1px] bg-[#E80245] -translate-y-1/2 transform rotate-[-3deg]"></div>
            </div>
          )}
        </div>

        {/* Product Name */}
        <h3
          className="text-sm font-normal text-gray-800 line-clamp-2 leading-snug cursor-pointer hover:text-primary transition-colors"
          onClick={handleProductClick}
        >
          {product.name}
        </h3>

        {/* Short Description */}
        <p className="text-xs text-gray-400 mt-0.5 mb-2">
          {product.customFields?.shortDescription || '400ml • 1 pcs'}
        </p>

        {/* Bulk Pricing Badges */}
        <div className="flex flex-col gap-0.5 mt-auto">
          {priceTiers.length > 0 ? (
            priceTiers.slice(0, 2).map((tier, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1 text-[#E80245] text-xs font-medium"
              >
                <BadgePercent size={12} className="flex-shrink-0" />
                <span>
                  <Price
                    priceWithTax={tier.price ?? 0}
                    currencyCode={currencyCode}
                    variant="primary"
                  />
                  /pack for {tier.minQty}+
                </span>
              </div>
            ))
          ) : (
            <div className="h-4 invisible" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
}
